/**
 * Hooks de mutación de **roles** (alta, edición, borrado).
 *
 * Cada mutación, en éxito, invalida la rama `['roles']` (lista + detalles) y
 * además la rama `['users']`: editar/borrar un rol cambia los permisos efectivos
 * de los usuarios con ese rol (espejo de `has_permission` que recalcula). Propaga
 * el `ApiError` para que la pantalla lo traduzca.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createRole, deleteRole, updateRole } from "../api";
import type { CreateRoleBody, Role, UpdateRoleBody } from "../types";
import { ROLES_ROOT_KEY, USERS_ROOT_KEY } from "./queryKeys";

/** Invalida la rama de roles y la de usuarios (permisos efectivos cambian). */
function useInvalidateRoles() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ROLES_ROOT_KEY });
    void queryClient.invalidateQueries({ queryKey: USERS_ROOT_KEY });
  };
}

/** Alta de rol personalizado (`POST /roles`). */
export function useCreateRole() {
  const invalidate = useInvalidateRoles();
  return useMutation<Role, Error, CreateRoleBody>({
    mutationFn: (body) => createRole(body),
    onSuccess: invalidate,
  });
}

/** Argumentos de edición: el id del rol + los cambios. */
interface UpdateRoleArgs {
  roleId: string;
  body: UpdateRoleBody;
}

/** Edición de rol (`PATCH /roles/<role_id>`). */
export function useUpdateRole() {
  const invalidate = useInvalidateRoles();
  return useMutation<Role, Error, UpdateRoleArgs>({
    mutationFn: ({ roleId, body }) => updateRole(roleId, body),
    onSuccess: invalidate,
  });
}

/** Borrado de rol personalizado (`DELETE /roles/<role_id>`). */
export function useDeleteRole() {
  const invalidate = useInvalidateRoles();
  return useMutation<void, Error, string>({
    mutationFn: (roleId) => deleteRole(roleId),
    onSuccess: invalidate,
  });
}
